import React, { useRef, useState, useMemo } from 'react'
import { SocialShareProps } from './SocialShare.types'
import './SocialShare.scss'
import { sanitizeHTML } from '../../../utils'
import { MaterialIcon } from '../../MaterialIcon/MaterialIcon'
import { MaterialIconId } from '../../MaterialIcon/MaterialIconId.type'
import { BrandIcon } from '../../Compounds/src/BrandIconCompound'
import { BrandIconId } from '../../Compounds/src/BrandIconCompound/src/BrandIconId.type'
import { BrandIconSize } from '../../Compounds/src/BrandIconCompound/src/BrandIconSize.type'
import { BrandIconColor } from '../../Compounds/src/BrandIconCompound/src/BrandIconColor.type'
import { Toast } from '../../Toast/Toast'
import { NotificationType } from '../../Notification/src/NotificationType.type'
import { usePageTitle } from '../../../utils/usePageTitle'

export const SocialShare = (props: SocialShareProps) => {
  const { copyLink, email, linkedIn, twitter, facebook, template } = props.content
  const emailButtonRef = useRef<HTMLButtonElement>(null!)
  const copyLinkButtonRef = useRef<HTMLButtonElement>(null!)
  const shareFacebookButtonRef = useRef<HTMLButtonElement>(null!)
  const shareXButtonRef = useRef<HTMLButtonElement>(null!)
  const shareLinkedInButtonRef = useRef<HTMLButtonElement>(null!)
  const [isCopyLinkToastVisible, setIsCopyLinkToastVisible] = useState(false)

  const pageTitle = usePageTitle()

  const replaceTitle = (url?: string) => url?.replace(/%title%/g, pageTitle) ?? ''

  const emailUrl = useMemo(() => replaceTitle(email?.url), [pageTitle, email?.url])
  const linkedInUrl = useMemo(() => replaceTitle(linkedIn?.url), [pageTitle, linkedIn?.url])
  const twitterUrl = useMemo(() => replaceTitle(twitter?.url), [pageTitle, twitter?.url])
  const facebookUrl = useMemo(() => replaceTitle(facebook?.url), [pageTitle, facebook?.url])

  const showCopyLinkToast = () => {
    setIsCopyLinkToastVisible(true)
  }

  const hideCopyLinkToast = () => {
    setIsCopyLinkToastVisible(false)
  }

  const metaDescription = useMemo(() => {
    return (document.querySelector('meta[name="description"]') as HTMLMetaElement)?.content || ''
  }, [])

  const handleCopyClick = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(showCopyLinkToast)
        .catch(err => {
          console.error('Unable to copy link', err)
        })
    } else {
      console.error('Copy to clipboard is not supported in this browser.')
    }
  }

  const handleShareButtonClick = (urlTemplate: string) => {
    const url = urlTemplate.replace('%description%', encodeURIComponent(metaDescription))
    window.open(url, '_blank')
  }

  const handleLabelButtonClick = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.click()
  }

  const handleMouseEnter = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.classList.add('is-open')
  }

  const handleMouseLeave = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.classList.remove('is-open')
  }

  if (
    copyLink?.hasCopyLink == 'false' &&
    email?.hasEmail == 'false' &&
    linkedIn?.hasLinkedIn == 'false' &&
    twitter?.hasTwitter == 'false' &&
    facebook?.hasFacebook == 'false'
  ) {
    return null
  }

  return (
    <section className={`SocialShare ${template === 'rightRail' ? 'SocialShare--rightRail' : ''}`}>
      <h4 className='SocialShare-heading u-textSizeMsrp2'>Share</h4>
      <ul className='SocialShare-list'>
        {email?.hasEmail === 'true' && (
          <li className='SocialShare-buttonWrapper u-marginBottom0'>
            <button
              ref={emailButtonRef}
              className='btn-social-email u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert2gu'
              data-analytics='button-analytics'
              aria-label={sanitizeHTML(email.name)}
              onClick={() => handleShareButtonClick(emailUrl)}
            >
              <MaterialIcon
                icon={MaterialIconId.mail}
                className='u-textColorActionBlue u-margin0'
                ariaLabel='Email'
              />
            </button>
            <button
              tabIndex={-1}
              className='SocialShare-label u-marginLeft2gu u-textNormal u-posRelative'
              onClick={() => handleLabelButtonClick(emailButtonRef)}
              onMouseEnter={() => handleMouseEnter(emailButtonRef)}
              onMouseLeave={() => handleMouseLeave(emailButtonRef)}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(email.name) }}
            ></button>
          </li>
        )}
        {copyLink?.hasCopyLink === 'true' && (
          <li className='SocialShare-buttonWrapper u-marginBottom0'>
            <button
              ref={copyLinkButtonRef}
              className='btn-social-copy u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert2gu'
              data-analytics={copyLink.analyticsName}
              aria-label={sanitizeHTML(copyLink.name)}
              onClick={handleCopyClick}
            >
              <MaterialIcon
                icon={MaterialIconId.link}
                className='u-textColorActionBlue u-margin0'
                ariaLabel='Copy'
              />
            </button>
            <button
              tabIndex={-1}
              className='SocialShare-label u-marginLeft2gu u-textNormal u-posRelative'
              onClick={() => handleLabelButtonClick(copyLinkButtonRef)}
              onMouseEnter={() => handleMouseEnter(copyLinkButtonRef)}
              onMouseLeave={() => handleMouseLeave(copyLinkButtonRef)}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(copyLink.name) }}
            ></button>
          </li>
        )}
        {facebook?.hasFacebook === 'true' && (
          <li className='SocialShare-buttonWrapper u-marginBottom0'>
            <button
              ref={shareFacebookButtonRef}
              className='btn-social-facebook u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert2gu'
              data-analytics={facebook.analyticsName}
              aria-label={sanitizeHTML(facebook.name)}
              onClick={() => handleShareButtonClick(facebookUrl)}
            >
              <BrandIcon
                className='SocialShare-svg'
                iconsDropdown={BrandIconId.socialFacebook}
                size={BrandIconSize.small}
              />
            </button>
            <button
              tabIndex={-1}
              className='SocialShare-label u-marginLeft2gu u-textNormal u-posRelative'
              onClick={() => handleLabelButtonClick(shareFacebookButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareFacebookButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareFacebookButtonRef)}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(facebook.name) }}
            ></button>
          </li>
        )}
        {twitter?.hasTwitter === 'true' && (
          <li className='SocialShare-buttonWrapper u-marginBottom0'>
            <button
              ref={shareXButtonRef}
              className='btn-social-twitter u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert2gu'
              data-analytics={twitter.analyticsName}
              aria-label={sanitizeHTML(twitter.name)}
              onClick={() => handleShareButtonClick(twitterUrl)}
            >
              <BrandIcon
                iconsColorsDropdown={BrandIconColor.actionBlue}
                className='SocialShare-svg u-fillColorActionBlue'
                iconsDropdown={BrandIconId.socialX}
                size={BrandIconSize.small}
              />
            </button>
            <button
              tabIndex={-1}
              className='SocialShare-label u-marginLeft2gu u-textNormal u-posRelative u-textCenter'
              onClick={() => handleLabelButtonClick(shareXButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareXButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareXButtonRef)}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(twitter.name) }}
            ></button>
          </li>
        )}
        {linkedIn?.hasLinkedIn === 'true' && (
          <li className='SocialShare-buttonWrapper u-marginBottom0'>
            <button
              ref={shareLinkedInButtonRef}
              className='btn-social-linkedin u-inlineBlock Button Button--secondary Button--secondary--action-blue SocialShare-button u-marginVert2gu'
              data-analytics={linkedIn.analyticsName}
              aria-label={sanitizeHTML(linkedIn.name)}
              onClick={() => handleShareButtonClick(linkedInUrl)}
            >
              <BrandIcon
                className='SocialShare-svg'
                iconsDropdown={BrandIconId.socialLinkedin}
                size={BrandIconSize.small}
              />
            </button>
            <button
              tabIndex={-1}
              className='SocialShare-label u-marginLeft2gu u-textNormal u-posRelative'
              onClick={() => handleLabelButtonClick(shareLinkedInButtonRef)}
              onMouseEnter={() => handleMouseEnter(shareLinkedInButtonRef)}
              onMouseLeave={() => handleMouseLeave(shareLinkedInButtonRef)}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(linkedIn.name) }}
            ></button>
          </li>
        )}
      </ul>
      <Toast
        message='Link copied.'
        type={NotificationType.CONFIRM}
        visible={isCopyLinkToastVisible}
        onClose={hideCopyLinkToast}
      />
    </section>
  )
}
